import { Add, Block, Download } from "@mui/icons-material";
import { Backdrop, Box, Button, Card, CardActionArea, CardContent, CardMedia, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomDialog } from "../../../common/components/CustomDialog";
import { default as CustomLoader } from "../../../common/components/CustomLoader";
import CustomLoadMore from "../../../common/components/CustomLoadMore";
import CustomSnackbar from "../../../common/components/CustomSnackbar";
import { getForm } from "../../../common/config/forms";
import { useAuth } from "../../../common/hooks/useAuth";
import { useLoading } from "../../../common/hooks/useLoading";
import { IBeehive, IBeehiveBody } from "../../../common/interfaces/beehive";
import { IExportStat } from "../../../common/interfaces/beehiveStats";
import { ICustomDialogProps } from "../../../common/interfaces/dialog";
import { ICustomOption } from "../../../common/interfaces/form";
import { ACTIONS } from "../../../common/interfaces/table";
import beehiveService from "../../../common/services/beehiveService";
import userService from "../../../common/services/userService";

const itemsPerPage = 12;

interface IBeehivesProps { }

const Beehives: FC<IBeehivesProps> = () => {

    const { loading } = useLoading();
    const navigate = useNavigate();
    const { permissionsState, loginData } = useAuth();
    const { t } = useTranslation();
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"))

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [beehives, setBeehives] = useState<IBeehive[]>([]);
    const [selectedUser, setSelectedUser] = useState<ICustomOption | undefined>(undefined);

    // forms
    const addBeehiveForm = getForm<IBeehiveBody>("addBeehive");
    const exportAllStatForm = getForm<IExportStat>("exportAllStat");
    const exportAllDataForm = getForm<IExportStat>("exportAllData");
    const [usersOptions, setUsersOptions] = useState<ICustomOption[]>([]);
    const [beehivesOptions, setBeehivesOptions] = useState<ICustomOption[]>([]);
    const [users, setUsers] = useState<ICustomOption[]>([]);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [close, setClose] = useState<boolean>(true);

    // dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [openExportDialog, setOpenExportDialog] = useState(false);
    const [openExportDataDialog, setOpenExportDataDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<React.ReactNode>(<></>);

    // load more controls
    const [count, setCount] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const getUsersOptionsList = async () => {
        try {
            const response = await userService.getAll();
            let options: ICustomOption[] = response.content.map(user => {
                return {
                    desc: user.emailAddress,
                    value: user.id
                }
            })
            let users: ICustomOption[] = response.content.map(user => {
                return {
                    desc: user.firstName + " " + user.lastName,
                    value: user.id
                }
            })
            setUsersOptions(options);
            setUsers(users)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    const getBeehivesList = useCallback(async (
        beehivesList: IBeehive[],
        total: number,
        count?: number,
    ) => {
        try {
            const response = count ?
                await beehiveService.getAll((count - 1) * itemsPerPage, itemsPerPage) :
                await beehiveService.getAll(0, total);
            let rows = response.content
            setTotalItems(response.pageInfo.totalItems);
            setBeehives([...beehivesList, ...rows]);
        } catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        } finally {
            setIsLoading(false);
        }
    }, [])

    const getAllBeehiveList = useCallback(async () => {
        try {
            const response = await beehiveService.getAll(0, 1000)
            setBeehivesOptions([
                ...response.content.map(beehive => {
                    return { value: beehive.id, desc: beehive.name }
                })
            ])
        } catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }, [])

    const handleAddAction = () => {
        getUsersOptionsList()
        setDialogTitle("beehivesPage.form.title");
        setDialogBody(undefined);
        setOpenDialog(true);
    };
    const handleExportAction = (action: ACTIONS) => {
        setClose(false);
        getAllBeehiveList();
        if (action === ACTIONS.EXPORT)
            setOpenExportDialog(true)
        else
            setOpenExportDataDialog(true)
    };

    useEffect(() => {
        if (permissionsState && permissionsState.existUsersPermissions && permissionsState.existViewUsersPermission)
            getUsersOptionsList()
    }, [permissionsState]);

    useEffect(() => {
        setIsLoading(true);
        if (selectedUser) {
            getBeehivesList([...beehives], totalItems);
        } else {
            getBeehivesList([...beehives], totalItems, count);
        }
    }, [selectedUser, getBeehivesList, count])

    // close dialog handler
    const handleClose = () => setOpenDialog(false);

    // api request handler
    const apiRequest = (data: IBeehiveBody | undefined) => {
        if (data) {
            return beehiveService.post(data);
        } else {
            return Promise.reject()
        }
    }

    const onSelectChange = (e: SelectChangeEvent) => {
        setCount(1);
        setBeehives([]);
        setSelectedUser(usersOptions.find(option => option.value === e.target.value))
    }

    // confirm dialog handler
    const handleActionConfirm = async (data: IBeehiveBody | undefined) => {
        handleClose();
        try {
            await apiRequest(data);
            setOpenSnackbar(true);
            setResponseType("success");
            setMessageSnackbar(t("genericSuccess"));
            getBeehivesList([], totalItems, count);
        } catch (error: unknown) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    const onExportClicked = async (data: IExportStat | undefined, source: "manual" | "import") => {
        if (data) {
            const res = await beehiveService.downloadCSV(source, data);
            if (typeof res === 'string' && res === 'OK') {
                setOpenSnackbar(true);
                setResponseType("success");
                setMessageSnackbar(t("genericSuccess"));
            } else {
                setOpenSnackbar(true);
                setResponseType("error");
                if (res instanceof AxiosError) {
                    setMessageSnackbar(res.response?.data.content.message)
                } else {
                    setMessageSnackbar(t("genericError"));
                }
            }
            setIsExporting(false);
        }
    }

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: "form",
        close: handleClose,
        confirm: () => handleActionConfirm(undefined),
        title: dialogTitle,
        open: openDialog,
        confirmButtonLabel: t("beehivesPage.form.saveChangesButton"),
        closeButtonLabel: t("beehivesPage.form.cancelButton"),
        fullWidth: true,
        formProps: {
            ...addBeehiveForm,
            inputs: [
                ...addBeehiveForm.inputs.map(input => {
                    if (input.name === "ownerId")
                        return { ...input, options: usersOptions }
                    else
                        return input
                })
            ],
            initialValues: {
                name: "",
                description: "",
                ownerId: ""
            },
            isDialog: true,
            submit: (data) => handleActionConfirm(data),
            customStyles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }
        }
    };

    useEffect(() => {
        if (!isExporting && close) {
            setOpenExportDialog(false);
            setOpenExportDataDialog(false);
            setBeehivesOptions([]);
        }
    }, [close, isExporting])

    // dialog props
    const dialogExportStatProps: ICustomDialogProps = {
        type: "form",
        close: () => {
            setClose(true);
        },
        confirm: () => { },
        title: t("beehiveDetailsPage.exportDialogTitle"),
        open: openExportDialog,
        confirmButtonLabel: t("beehiveDetailsPage.exportButtonLabel"),
        closeButtonLabel: t("beehiveDetailsPage.tabs.statsForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...exportAllStatForm,
            initialValues: {
                houseIds: [],
                from: null,
                to: null,
                
                startingSpringDeposition: true,
                colonyStrength: true,
                broodQuality: true,
                stockQuality: true,
                eatingType: true,
                looting: true,
                orphanage: true,
                varroaPresence: true,
                varroaTreatment: true,
                varroaDeathAfterTreatment: true,
                stationaryInterference: true,
                otherSanitaryProblem: true,
                mainFlower: true,
                swarming: true,
            },
            inputs: [
                ...exportAllStatForm.inputs.map(input => {
                    if (input.name === "houseIds")
                        return { ...input, options: beehivesOptions }
                    else
                        return input
                })
            ],
            isDialog: true,
            submit: (data) => {
                setIsExporting(true);
                onExportClicked(data, "manual")
            },
            customStyles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
            }
        }
    };

    // dialog props
    const dialogExportDataProps: ICustomDialogProps = {
        type: "form",
        close: () => {
            setClose(true);
        },
        confirm: () => { },
        title: t("beehiveDetailsPage.exportDataDialogTitle"),
        open: openExportDataDialog,
        confirmButtonLabel: t("beehiveDetailsPage.exportButtonLabel"),
        closeButtonLabel: t("beehiveDetailsPage.tabs.statsForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...exportAllDataForm,
            initialValues: {
                houseIds: [],
                from: null,
                to: null,
                temperature: true,
                wet: true,
                weight: true,
                flightsNumber: true,
                coordinates: true,
            },
            inputs: [
                ...exportAllDataForm.inputs.map(input => {
                    if (input.name === "houseIds")
                        return { ...input, options: beehivesOptions }
                    else
                        return input
                })
            ],
            isDialog: true,
            submit: (data) => {
                setIsExporting(true);
                onExportClicked(data, "import");
            },
            customStyles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
            }
        }
    };

    return <>
        {isExporting && <Backdrop
            open
            sx={{
                position: "absolute",
                color: '#fff',
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: (theme) => theme.zIndex.modal + 1,
                width: "100%",
                height: "100%"
            }}
        >
            <Box sx={{
                position: "relative",
                width: { xs: 200, sm: 300, md: 400 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}>
                <CustomLoader />
            </Box>
        </Backdrop>}
        <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
            <CustomSnackbar
                type={responseType}
                message={messageSnackbar}
                open={openSnackbar}
                onClose={() => setOpenSnackbar(false)}
            />
            <CustomDialog {...dialogProps}>{dialogBody}</CustomDialog>
            <CustomDialog {...dialogExportStatProps}>{dialogBody}</CustomDialog>
            <CustomDialog {...dialogExportDataProps}>{dialogBody}</CustomDialog>
            <Box sx={{ display: "flex", flexDirection: largeMediumScreen ? "row" : "column", justifyContent: largeMediumScreen ? "space-between" : "center", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ mb: largeMediumScreen ? 0 : 2 }}>
                    {t("beehivesPage.title")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: largeMediumScreen ? "row" : "column", justifyContent: "flex-end" }}>
                    {
                        permissionsState &&
                        permissionsState.existCreateBeehivesPermission &&
                        <Button variant="contained"
                            sx={{
                                mr: largeMediumScreen ? 2 : 0,
                                mb: smallScreen ? 1 : 0,
                            }}
                            onClick={() => handleAddAction()}
                        >
                            <Add sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                            {t("beehivesPage.newBeehiveButton")}
                        </Button>
                    }
                    {
                        permissionsState &&
                        permissionsState.existDownloadAllBeehivesPermission &&
                        loginData &&
                        <Button variant="outlined" color="secondary"
                            sx={{
                                mr: largeMediumScreen ? 2 : 0,
                                mb: smallScreen ? 1 : 0,
                            }}
                            onClick={() => handleExportAction(ACTIONS.EXPORT)}
                        >
                            <Download sx={{ mr: !largeMediumScreen ? 0 : 0.5 }} />
                            {t("beehiveDetailsPage.tabs.exportStatsButtonLabel")}
                        </Button>
                    }
                    {
                        permissionsState &&
                        permissionsState.existDownloadAllBeehivesPermission &&
                        loginData &&
                        <Button variant="outlined" color="secondary"
                            onClick={() => handleExportAction(ACTIONS.EXPORT_DATA)}
                        >
                            <Download sx={{ mr: !largeMediumScreen ? 0 : 0.5 }} />
                            {t("beehiveDetailsPage.tabs.exportDataButtonLabel")}
                        </Button>
                    }
                </Box>
            </Box>
            {
                permissionsState &&
                permissionsState.existViewUsersPermission &&
                <FormControl size="small" sx={{ minWidth: largeMediumScreen ? 300 : "100%", mb: 2 }}>
                    <InputLabel id="period-select-label">{t("beehivesPage.selectUserPlaceholder")}</InputLabel>
                    <Select
                        id="user-select"
                        value={selectedUser?.value.toString() || ""}
                        name="user"
                        onChange={onSelectChange}
                        labelId="user-select-label"
                        label={t("beehivesPage.selectUserPlaceholder")}
                        MenuProps={{
                            style: {
                                maxHeight: 400,
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>{t("beehivesPage.genericSelectLabel")}</em>
                        </MenuItem>
                        {
                            users && users.map((option) => (
                                <MenuItem
                                    key={option.value + "-" + option.desc}
                                    value={option.value}
                                >
                                    {option.desc}
                                </MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            }
            {
                loading && !openDialog && !openExportDialog && !openExportDataDialog && count === 1 ? <CustomLoader /> :
                    <>
                        <Grid container rowSpacing={2} columnSpacing={2}>
                            {
                                (selectedUser ? beehives.filter(beehive => beehive.ownerId === selectedUser.value) : beehives).map(
                                    (beehive) =>
                                        <Grid key={beehive.id} item xs={12} sm={6} md={4} lg={3} xl={3}>
                                            <Card>
                                                <CardActionArea component="a" onClick={(event) => {
                                                    event.stopPropagation();
                                                    navigate(beehive.id)
                                                }}>
                                                    <CardMedia
                                                        sx={{ height: 160, objectFit: "contain" }}
                                                        image={beehive.previewUrl}
                                                        title="green iguana"
                                                    />
                                                    <CardContent sx={{ py: 1.5 }}>
                                                        <Typography gutterBottom variant="h6" component="div" sx={{
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            mb: 0
                                                        }}>
                                                            {beehive.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {beehive.externalProviderCode} - {beehive.externalProviderId}
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>

                                )
                            }
                        </Grid>
                        {
                            (selectedUser ? beehives.filter(beehive => beehive.ownerId === selectedUser.value) : beehives).length === 0 &&
                            <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: "center", mt: 2, ml: 1 }}>
                                <Block sx={{ mr: 1 }} />
                                {t("beehivesPage.noDataLabel")}
                            </Typography>
                        }
                        {
                            !selectedUser &&
                            Math.round(totalItems / itemsPerPage) >= count &&
                            totalItems > itemsPerPage &&
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <CustomLoadMore count={count} setCount={setCount} loading={isLoading} />
                            </Box>
                        }
                    </>
            }
        </Box>
    </>

}

export default Beehives;